import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import ProprietaireFormModal from "./ProprietaireFormModal";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "./ui/table";
import { Button } from "./ui/button";
import { BsPersonFill } from "react-icons/bs";

// Couleurs par défaut si Firebase n'est pas disponible
const DEFAULT_TAG_COLORS = [
  { id: 1, nom: 'Bleu', codeHex: '#2563eb' },
  { id: 2, nom: 'Rouge', codeHex: '#ef4444' },
  { id: 3, nom: 'Vert', codeHex: '#22c55e' },
  { id: 4, nom: 'Jaune', codeHex: '#eab308' },
  { id: 5, nom: 'Orange', codeHex: '#f97316' },
  { id: 6, nom: 'Violet', codeHex: '#8b5cf6' },
  { id: 7, nom: 'Rose', codeHex: '#ec4899' },
  { id: 8, nom: 'Turquoise', codeHex: '#06b6d4' },
  { id: 9, nom: 'Gris', codeHex: '#6b7280' },
  { id: 10, nom: 'Noir', codeHex: '#111827' },
  { id: 11, nom: 'Marron', codeHex: '#92400e' },
  { id: 12, nom: 'Blanc', codeHex: '#f3f4f6' }
];

export default function ProprietaireList() {
  const [proprietaires, setProprietaires] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tagColors, setTagColors] = useState([]);

  useEffect(() => {
    fetchProprietaires();
    fetchTagColors();
  }, []);

  async function fetchProprietaires() {
    const snap = await getDocs(collection(db, "proprietaires"));
    setProprietaires(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }

  function handleEdit(proprio) {
    setEditing(proprio);
  }
  function handleAdd() {
    setEditing({});
  }
  function handleClose() {
    setEditing(null);
    fetchProprietaires();
  }

  // Charger les couleurs de tags depuis Firebase (même système que les animaux)
  async function fetchTagColors() {
    try {
      const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
      let colorsData = [];
      
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        if (data.id && data.codeHex) {
          colorsData.push({ 
            id: data.id,
            nom: data.nom || '',
            codeHex: data.codeHex,
            ordre: data.ordre || data.id
          });
        }
      });

      // Trier par ordre
      colorsData.sort((a, b) => a.ordre - b.ordre);

      // Si pas de données Firebase, utiliser les couleurs par défaut
      if (colorsData.length === 0) {
        console.log('Aucune couleur trouvée dans tagCouleursAnimaux, utilisation des couleurs par défaut');
        colorsData = DEFAULT_TAG_COLORS;
      }

      setTagColors(colorsData);
    } catch (error) {
      console.error('Erreur lors du chargement des couleurs depuis tagCouleursAnimaux:', error);
      setTagColors(DEFAULT_TAG_COLORS);
    }
  }

  // Fonction pour obtenir la couleur du tag propriétaire
  function getProprietaireTagColor(proprietaire) {
    // Nouveau système avec tagCouleur (ID)
    if (proprietaire.tagCouleur) {
      const numericId = parseInt(proprietaire.tagCouleur);
      if (!isNaN(numericId)) {
        const found = tagColors.find(tc => tc.id === numericId);
        if (found) return found.codeHex;
      }
    }
    
    // Ancien système avec tagCouleurNom (pour rétrocompatibilité)
    if (proprietaire.tagCouleurNom) {
      const found = tagColors.find(tc => tc.nom === proprietaire.tagCouleurNom);
      if (found) return found.codeHex;
    }
    
    return null;
  }

  // Fonction pour obtenir le nom à afficher
  function getProprietaireTagDisplayName(proprietaire) {
    // Nouveau système
    if (proprietaire.tagCouleur) {
      const numericId = parseInt(proprietaire.tagCouleur);
      if (!isNaN(numericId)) {
        const found = tagColors.find(tc => tc.id === numericId);
        if (found) return `${found.nom} (ID: ${found.id})`;
      }
    }
    
    // Ancien système
    if (proprietaire.tagCouleurNom) {
      return proprietaire.tagCouleurNom;
    }
    
    return '';
  }

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-4">
        <Button onClick={handleAdd} variant="default">
          + Nouveau propriétaire
        </Button>
      </div>
      
      <Table className="w-full table-auto md:table-fixed">
        <colgroup>
          <col style={{ width: "6%" }} />   {/* Tag */}
          <col style={{ width: "8%" }} />   {/* Civilité */}
          <col style={{ width: "14%" }} />  {/* Nom */}
          <col style={{ width: "14%" }} />  {/* Prénom */}
          <col style={{ width: "14%" }} />  {/* Ville */}
          <col style={{ width: "20%" }} />  {/* E-mail */}
          <col style={{ width: "14%" }} />  {/* Téléphone */}
          <col style={{ width: "10%" }} />  {/* Actions */}
        </colgroup>

        <TableHeader>
          <TableRow>
            <TableHead className="p-2 w-16 text-left">Tag</TableHead>
            <TableHead className="p-2 w-20 text-left">Civilité</TableHead>
            <TableHead className="p-2 w-40 text-left">Nom</TableHead>
            <TableHead className="p-2 w-40 text-left">Prénom</TableHead>
            <TableHead className="p-2 w-40 text-left">Ville</TableHead>
            <TableHead className="p-2 w-56 text-left">E-mail</TableHead>
            <TableHead className="p-2 w-40 text-left">Téléphone</TableHead>
            <TableHead className="p-2 w-24 text-right"></TableHead>
          </TableRow>
        </TableHeader>
        
        <TableBody>
          {proprietaires.map(proprio => {
            const tagColor = getProprietaireTagColor(proprio);
            const tagDisplayName = getProprietaireTagDisplayName(proprio);
            
            return (
              <TableRow key={proprio.id} className="border-t">
                <TableCell className="p-2">
                  {tagColor ? (
                    <BsPersonFill 
                      className="w-5 h-5" 
                      style={{ color: tagColor }}
                      title={tagDisplayName}
                    />
                  ) : (
                    (proprio.tagCouleur || proprio.tagCouleurNom) && (
                      <span 
                        className="text-xs text-gray-500"
                        title={`Tag: ${proprio.tagCouleur || proprio.tagCouleurNom} (couleur non trouvée)`}
                      >
                        {proprio.tagCouleur || proprio.tagCouleurNom}
                      </span>
                    )
                  )}
                </TableCell>
                <TableCell className="p-2 whitespace-nowrap">{proprio.civilite || "-"}</TableCell>
                <TableCell className="p-2 truncate max-w-[200px]">{proprio.nom || "-"}</TableCell>
                <TableCell className="p-2 truncate max-w-[200px]">{proprio.prenom || "-"}</TableCell>
                <TableCell className="p-2 truncate max-w-[180px]">{proprio.ville || "-"}</TableCell>
                <TableCell className="p-2 truncate max-w-[260px]">{proprio.email || "-"}</TableCell>
                <TableCell className="p-2">{proprio.indicatifTel1 || ""} {proprio.telephone1 || ""}</TableCell>
                <TableCell className="p-2">
                  <Button onClick={() => handleEdit(proprio)} variant="default">
                    Éditer
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      {editing && (
        <ProprietaireFormModal
          proprietaire={editing}
          onClose={handleClose}
        />
      )}
    </div>
  );
}